import React from "react";
import { translateService, dateHelperService } from "../../services/imports";
import shareService from "../../services/shareService";
import signService from "../../services/signService";
import { LoadingButton } from "../../components/formcontrols/imports";
import { requiredSignatoryParts } from "./signingMethods";

const DECLINE_REASON_MAX = 1000;

// The recipient's page (share-prompt task 10): the read's `share` block -
// who shared, what is asked, the deadline, why signing is closed, the
// sharer's message as plain text - the required signatory from
// signing-options (masked), and decline. A share with a signer match (delta
// 7) and no signing button says why (matchProblem, from the document page):
// "none" - the gateway lists no signing tool of the sharer meeting it, so
// the sharer must add one; "notHere" - it lists one this site cannot use,
// which is no fault of the sharer.
export default class ShareBanner extends React.Component {
  state = { declining: false, reason: "", busy: false, error: null };

  t = (k, ...a) => translateService.t(`share.${k}`, ...a);

  // only a SIGN share whose signing is open and that grants something - or
  // gives a signer match; a share made before the rewrite has no grants and
  // cannot be declined
  canDecline() {
    const { share, options } = this.props;
    return (
      share.permission === "SIGN" &&
      share.signingOpen &&
      !!options &&
      (Array.isArray(options.grantedSigners) || Array.isArray(options.grantedKinds) || !!options.signerMatch)
    );
  }

  decline = () => {
    const { docId, share, onChanged } = this.props;
    this.setState({ busy: true, error: null });
    shareService.decline(docId, share.shareId, this.state.reason.trim() || null).subscribe(
      () => {
        this.setState({ busy: false, declining: false, reason: "" });
        if (onChanged) onChanged();
      },
      (e) => this.setState({ busy: false, error: signService.problemMessage(e, this.t("declineError")) })
    );
  };

  render() {
    const { share, options } = this.props;
    const t = this.t;
    const name = share.sharerName;
    const sign = share.permission === "SIGN";
    const heading = sign
      ? name ? t("asksToSign", name) : t("asksToSignNoName")
      : name ? t("sharedWithYou", name) : t("sharedWithYouNoName");

    let closed = null;
    if (share.closedReason === "DEADLINE_PASSED") closed = t("deadlinePassed");
    else if (share.closedReason === "DECLINED") closed = t("declined");
    else if (share.closedReason === "READ_ONLY" && sign) closed = t("signingNotHere");

    const required = requiredSignatoryParts(options && options.requiredSignatory);
    const country = required && (translateService.t(`share.country_${required.country}`) || required.country);

    return (
      <div className="share-banner" role="region" aria-label={t("bannerLabel")}>
        <p className="share-banner__heading">{heading}</p>
        {share.deadline && (
          <p>
            {t("deadline", dateHelperService.format(share.deadline, dateHelperService.dateTimeFormat, false, true))}
          </p>
        )}
        {required && (
          <p>
            {required.personalNumber
              ? t("requiredSignatory", country, required.personalNumber)
              : t("requiredSignatoryHidden", country)}
          </p>
        )}
        {closed && <p className="share-banner__closed">{closed}</p>}
        {!closed && this.props.matchProblem === "none" && (
          <p className="share-banner__closed">
            {name
              ? translateService.t("signerMatch.noneMatchesNamed", name)
              : translateService.t("signerMatch.noneMatches")}
          </p>
        )}
        {!closed && this.props.matchProblem === "notHere" && (
          <p className="share-banner__closed">{t("signingNotHere")}</p>
        )}
        {share.message && <blockquote className="share-banner__message">{share.message}</blockquote>}

        {this.canDecline() &&
          (this.state.declining ? (
            <div className="share-banner__decline">
              <label htmlFor="share-decline-reason">{t("declineReason")}</label>
              <textarea
                id="share-decline-reason"
                maxLength={DECLINE_REASON_MAX}
                value={this.state.reason}
                onChange={(e) => this.setState({ reason: e.target.value })}
              />
              {this.state.error && <p className="share-banner__error">{this.state.error}</p>}
              <div className="buttons">
                <LoadingButton
                  type="button"
                  className="button background-color-three color-white"
                  loading={this.state.busy}
                  onClick={this.decline}
                >
                  {t("declineSend")}
                </LoadingButton>
                <LoadingButton
                  type="button"
                  className="button background-color-one color-white"
                  disabled={this.state.busy}
                  onClick={() => this.setState({ declining: false, error: null })}
                >
                  {t("cancel")}
                </LoadingButton>
              </div>
            </div>
          ) : (
            <div className="buttons">
              <LoadingButton
                type="button"
                className="button background-color-three color-white"
                onClick={() => this.setState({ declining: true })}
              >
                {t("decline")}
              </LoadingButton>
            </div>
          ))}
      </div>
    );
  }
}
